import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { IntelCommentBreakdown } from '../model/contracts'

type BreakdownCategory = keyof IntelCommentBreakdown

const BAR_COLOR: Record<BreakdownCategory, string> = {
  harsh: 'bg-destructive',
  abusive: 'bg-destructive/60',
  constructive: 'bg-sentiment-positive',
  supportive: 'bg-sentiment-positive/70',
}

const CATEGORIES: BreakdownCategory[] = ['harsh', 'abusive', 'constructive', 'supportive']

function useDrawerEscKey(onClose: () => void, open: boolean) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])
}

interface CategoryDrawerProps {
  category: BreakdownCategory | null
  count: number
  onClose: () => void
}

function CategoryDrawer({ category, count, onClose }: CategoryDrawerProps) {
  const { t } = useTranslation('intel')
  const open = category !== null
  const backdropRef = useRef<HTMLDivElement>(null)

  useDrawerEscKey(onClose, open)

  const categoryLabel = category ? t(`breakdown.${category}`) : ''

  return (
    <>
      <div
        ref={backdropRef}
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/30 transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={categoryLabel}
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col bg-card shadow-xl transition-transform duration-200 ease-in-out',
          'border-l border-border',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-base font-semibold text-foreground">
              {category
                ? t('breakdown.drawerTitle', { category: categoryLabel, count })
                : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('breakdown.close')}
            className="mt-0.5 shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <p className="text-sm text-muted-foreground">{t('breakdown.comingSoon')}</p>
        </div>
      </aside>
    </>
  )
}

interface IntelCommentBreakdownProps {
  breakdown: IntelCommentBreakdown
}

export function IntelCommentBreakdown({ breakdown }: IntelCommentBreakdownProps) {
  const { t } = useTranslation('intel')
  const [activeCategory, setActiveCategory] = useState<BreakdownCategory | null>(null)

  const maxValue = Math.max(
    breakdown.harsh,
    breakdown.abusive,
    breakdown.constructive,
    breakdown.supportive,
  )

  return (
    <>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
        {CATEGORIES.map((category) => {
          const value = breakdown[category]
          const widthPct = maxValue > 0 ? Math.round((value / maxValue) * 100) : 0

          return (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              className="flex flex-col gap-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t(`breakdown.${category}`)}</span>
                <span>{value}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    BAR_COLOR[category],
                  )}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </button>
          )
        })}
      </div>

      <CategoryDrawer
        category={activeCategory}
        count={activeCategory ? breakdown[activeCategory] : 0}
        onClose={() => setActiveCategory(null)}
      />
    </>
  )
}
